import { createStore } from '@/lib/storeCreator';
import { projectsApi } from '@/features/projects/services';
import { sitesApi } from '@/features/sites/services';
import { getErrorMessage } from '@/types/api';
import { t } from 'i18next';
import type {
  Project,
  ProjectCreation,
  UpdateDataProject,
} from '@/features/projects/types';
import type { MaterialListFormat } from '@/features/sites/types';

export interface ActionResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface ProjectsStoreState {
  // Projects data
  projects: Project[];
  currentProject: Project | null;

  // Material list formats
  materialListFormats: MaterialListFormat[];

  // Unified loading states using state machine pattern
  loading: {
    projects:
      | 'idle'
      | 'fetching'
      | 'creating'
      | 'updating'
      | 'deleting'
      | 'error';
    formats: 'idle' | 'fetching' | 'error';
  };

  // Error states
  projectsError: string | null;
  projectError: string | null;
  formatsError: string | null;
}

interface ProjectsStoreActions {
  // Projects actions
  getProjects: () => Promise<void>;
  getProject: (projectId: number) => Promise<void>;
  createProject: (
    projectData: ProjectCreation
  ) => Promise<ActionResult<{ id: number }>>;
  updateProject: (updateData: UpdateDataProject) => Promise<ActionResult>;
  deleteProject: (projectId: number) => Promise<ActionResult>;

  // Material list formats actions
  getMaterialListFormats: () => Promise<void>;
  getProjectMaterialListFormats: (projectId?: number) => Promise<unknown>;

  // Error actions
  clearErrors: () => void;
  clearProjectsError: () => void;
  clearProjectError: () => void;
  clearFormatsError: () => void;

  // Legacy compatibility helpers (computed properties)
  isLoading: () => boolean;
  isProjectsLoading: () => boolean;
  isProjectLoading: () => boolean;
  isFormatsLoading: () => boolean;
}

export const useProjectsStore = createStore<
  ProjectsStoreState,
  ProjectsStoreActions
>(
  {
    state: {
      projects: [],
      currentProject: null,
      materialListFormats: [],
      loading: {
        projects: 'idle',
        formats: 'idle',
      },
      projectsError: null,
      projectError: null,
      formatsError: null,
    },
    actions: (set, get) => ({
      // Projects actions - NO try/catch needed, centralized API handles errors
      getProjects: async () => {
        set(state => {
          state.loading.projects = 'fetching';
          state.projectsError = null;
        });

        const response = await projectsApi.getProjects();

        if (response.error || !response.isSuccess) {
          set(state => {
            state.projects = [];
            state.loading.projects = 'error';
            state.projectsError = response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch projects';
          });
          return;
        }

        set(state => {
          state.projects = response.result.data || [];
          state.loading.projects = 'idle';
          state.projectsError = null;
        });
      },

      getProject: async (projectId: number) => {
        set(state => {
          state.loading.projects = 'fetching';
          state.projectError = null;
        });

        const response = await projectsApi.getProject(projectId);

        if (response.error || !response.isSuccess) {
          set(state => {
            state.currentProject = null;
            state.loading.projects = 'error';
            state.projectError = response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch project';
          });
          return;
        }

        set(state => {
          state.currentProject = response.result.data;
          state.loading.projects = 'idle';
          state.projectError = null;
        });
      },

      createProject: async (
        projectData: ProjectCreation
      ): Promise<ActionResult<{ id: number }>> => {
        set(state => {
          state.loading.projects = 'creating';
          state.projectsError = null;
        });

        const response = await projectsApi.createProject(projectData);

        if (response.error || !response.isSuccess) {
          const errorMessage = response.error
            ? getErrorMessage(response.error)
            : 'Failed to create project';
          set(state => {
            state.projectsError = errorMessage;
            state.loading.projects = 'error';
          });
          return { success: false, error: errorMessage };
        }

        // Refresh projects list after creation
        await get().actions.getProjects();

        set(state => {
          state.loading.projects = 'idle';
        });

        return {
          success: true,
          data: response.result,
          message: t('project_successfully_created_toast_text'),
        };
      },

      updateProject: async (updateData: UpdateDataProject) => {
        set(state => {
          state.loading.projects = 'updating';
          state.projectError = null;
        });

        const response = await projectsApi.updateProject(updateData);

        if (response.error || !response.isSuccess) {
          const errorMessage = response.error
            ? getErrorMessage(response.error)
            : 'Failed to update project';
          set(() => ({
            projectError: errorMessage,
            loading: { ...get().loading, projects: 'error' },
          }));
          return { success: false, error: errorMessage };
        }

        // Refresh project data after update
        if (updateData.id) {
          await get().actions.getProject(updateData.id);
        }

        set(state => {
          state.loading.projects = 'idle';
        });

        return {
          success: true,
          data: response.result,
          message: t('project_successfully_updated_toast_text'),
        };
      },

      deleteProject: async (projectId: number) => {
        set(state => {
          state.loading.projects = 'deleting';
          state.projectsError = null;
        });

        const response = await projectsApi.deleteProject(projectId);

        if (response.error || !response.isSuccess) {
          const errorMessage = response.error
            ? getErrorMessage(response.error)
            : 'Failed to delete project';
          set(state => {
            state.projectsError = errorMessage;
            state.loading.projects = 'error';
          });
          return { success: false, error: errorMessage };
        }

        // Refresh projects list after deletion
        await get().actions.getProjects();

        set(state => {
          state.loading.projects = 'idle';
        });

        return { success: true };
      },

      // Material list formats actions
      getMaterialListFormats: async () => {
        set(state => {
          state.loading.formats = 'fetching';
          state.formatsError = null;
        });

        const response = await sitesApi.getMaterialListFormats();

        if (response.error || !response.isSuccess) {
          set(state => {
            state.loading.formats = 'error';
            state.formatsError = response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch material list formats';
          });
          return;
        }

        set(state => {
          state.materialListFormats = response.result?.data || [];
          state.loading.formats = 'idle';
          state.formatsError = null;
        });
      },

      getProjectMaterialListFormats: async (projectId?: number) => {
        set(state => {
          state.loading.formats = 'fetching';
          state.formatsError = null;
        });

        const response =
          await sitesApi.getProjectMaterialListFormats(projectId);

        if (response.error || !response.isSuccess) {
          set(state => {
            state.loading.formats = 'error';
            state.formatsError = response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch project material list formats';
          });
          return null;
        }

        set(state => {
          state.loading.formats = 'idle';
          state.formatsError = null;
        });
        return response.result?.data || null;
      },

      // Error actions
      clearErrors: () => {
        set(state => {
          state.projectsError = null;
          state.projectError = null;
          state.formatsError = null;
        });
      },

      clearProjectsError: () => {
        set(state => {
          state.projectsError = null;
        });
      },

      clearProjectError: () => {
        set(state => {
          state.projectError = null;
        });
      },

      clearFormatsError: () => {
        set(state => {
          state.formatsError = null;
        });
      },

      // Legacy compatibility helpers (computed properties)
      isLoading: () => {
        const { loading } = get();
        return loading.projects !== 'idle' || loading.formats !== 'idle';
      },

      isProjectsLoading: () => {
        const { loading } = get();
        return loading.projects !== 'idle';
      },

      isProjectLoading: () => {
        const { loading } = get();
        return loading.projects !== 'idle';
      },

      isFormatsLoading: () => {
        const { loading } = get();
        return loading.formats !== 'idle';
      },
    }),
  },
  'ProjectsStore'
);
